import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTraces } from "@/lib/hooks/useTraces";
import { groupSpansByTrace } from "@/utils/trace-utils";
import { useLocation } from "@tanstack/react-router";
import {
  // Timer, TrendingDown, TrendingUp,
  ChevronDown,
  ChevronRight,
  SortDesc,
  Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { DockmasterSpan } from "../../../../schemas/codegen";
import type {
  EnhancedSpan,
  EnhancedTrace,
} from "../../CodeGen/Chat/components/traces/types";
import { McpRequestTraces } from "../McpRequestTraces";

interface ProjectObservabilityTabProps {
  projectId: string;
}

export function ProjectObservabilityTab({
  projectId,
}: ProjectObservabilityTabProps) {
  // Get search parameters to check for specific trace to expand
  const location = useLocation();
  const expandedTraceId =
    new URLSearchParams(location.search).get("expandedTrace") || undefined;

  // State for expandable tools
  const [expandedTool, setExpandedTool] = useState<string | null>(null);

  // State for sorting grouped traces
  const [sortBy, setSortBy] = useState<"calls" | "recent" | "duration">(
    "recent",
  );

  // Fetch trace data
  const {
    data: tracesData,
    isLoading: tracesLoading,
    error: tracesError,
  } = useTraces(projectId);

  // Process traces and compute analytics
  const { enhancedTraces, toolAnalytics } = useMemo(() => {
    const rawTraces = tracesData?.traces ?? [];

    // Process traces using utility functions
    const enhancedTraces: EnhancedTrace[] = rawTraces.map((trace) => {
      if (!trace.spans || trace.spans.length === 0) {
        return {
          traceId: trace.traceId,
          startTime: 0,
          endTime: 0,
          duration: 0,
          spanCount: 0,
          hasErrors: false,
          spans: [],
        };
      }

      // Use utility to process the trace (cast spans to correct type)
      const [processedTrace] = groupSpansByTrace(
        trace.spans as DockmasterSpan[],
      );
      return (
        processedTrace || {
          traceId: trace.traceId,
          startTime: 0,
          endTime: 0,
          duration: 0,
          spanCount: 0,
          hasErrors: false,
          spans: [],
        }
      );
    });

    // Compute tool analytics from traces
    const toolMap = new Map<
      string,
      {
        calls: number;
        totalDuration: number;
        errors: number;
        latestTraceTime: number;
      }
    >();
    let totalRequests = 0;
    let totalErrors = 0;
    const responseTimes: number[] = [];

    for (const trace of enhancedTraces) {
      totalRequests++;

      if (trace.hasErrors) {
        totalErrors++;
      }

      responseTimes.push(trace.duration);

      for (const span of trace.spans as EnhancedSpan[]) {
        const toolName = span.mcpToolName || span.mcpMethodName;

        if (toolName) {
          const current = toolMap.get(toolName) || {
            calls: 0,
            totalDuration: 0,
            errors: 0,
            latestTraceTime: 0,
          };
          current.calls++;
          current.totalDuration += span.duration || 0;
          current.latestTraceTime = Math.max(
            current.latestTraceTime,
            trace.startTime,
          );

          if (span.status === "error") {
            current.errors++;
          }

          toolMap.set(toolName, current);
        }
      }
    }

    // Convert to analytics format
    const toolAnalytics = Array.from(toolMap.entries())
      .map(([name, stats]) => ({
        name,
        calls: stats.calls,
        avgDuration:
          stats.calls > 0 ? Math.round(stats.totalDuration / stats.calls) : 0,
        successRate:
          stats.calls > 0
            ? Number(
                (((stats.calls - stats.errors) / stats.calls) * 100).toFixed(1),
              )
            : 100.0,
      }))
      .sort((a, b) => {
        if (sortBy === "calls") {
          // Sort by call count (descending)
          return b.calls - a.calls;
        }

        if (sortBy === "duration") {
          // Sort by average duration (descending - slowest first)
          return b.avgDuration - a.avgDuration;
        }

        // Default: sort by latest trace time (descending - most recent first)
        const aLatestTime = toolMap.get(a.name)?.latestTraceTime || 0;
        const bLatestTime = toolMap.get(b.name)?.latestTraceTime || 0;
        return bLatestTime - aLatestTime;
      });

    // Calculate metrics
    responseTimes.sort((a, b) => a - b);
    const p50 = responseTimes[Math.floor(responseTimes.length * 0.5)] || 0;
    const p95 = responseTimes[Math.floor(responseTimes.length * 0.95)] || 0;
    const p99 = responseTimes[Math.floor(responseTimes.length * 0.99)] || 0;
    const errorRate =
      totalRequests > 0
        ? Number(((totalErrors / totalRequests) * 100).toFixed(1))
        : 0;

    const mcpMetrics = {
      responseTime: {
        p50: Math.round(p50),
        p95: Math.round(p95),
        p99: Math.round(p99),
      },
      requestVolume: {
        today: totalRequests,
        trend: "+0%", // TODO: Calculate trend when historical data available
      },
      errorRate: {
        rate: errorRate,
        trend: "-0%", // TODO: Calculate trend when historical data available
        count: totalErrors,
      },
    };

    return { enhancedTraces, toolAnalytics, mcpMetrics };
  }, [tracesData, sortBy]);

  // Filter traces by tool name
  const getFilteredTraces = (toolName: string) => {
    return enhancedTraces.filter((trace) =>
      trace.spans.some(
        (span) =>
          (span as EnhancedSpan).mcpToolName === toolName ||
          (span as EnhancedSpan).mcpMethodName === toolName,
      ),
    );
  };

  const toggleTool = (toolName: string) => {
    setExpandedTool(expandedTool === toolName ? null : toolName);
  };

  return (
    <div className="space-y-6 @container">
      {/* MCP Performance Overview */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Requests Today (dummy data)
                </p>
                <p className="text-2xl font-bold">
                  {mcpMetrics.requestVolume.today}
                </p>
              </div>
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <p className="text-xs text-success mt-1">
              {mcpMetrics.requestVolume.trend} vs yesterday
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  P95 Response (dummy data)
                </p>
                <p className="text-2xl font-bold">
                  {mcpMetrics.responseTime.p95}ms
                </p>
              </div>
              <Timer className="w-5 h-5 text-warning" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              95th percentile
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Error Rate (dummy data)
                </p>
                <p className="text-2xl font-bold">
                  {mcpMetrics.errorRate.rate}%
                </p>
              </div>
              {mcpMetrics.errorRate.trend.startsWith("-") ? (
                <TrendingDown className="w-5 h-5 text-success" />
              ) : (
                <TrendingUp className="w-5 h-5 text-destructive" />
              )}
            </div>
            <p
              className={`text-xs mt-1 ${
                mcpMetrics.errorRate.trend.startsWith("-")
                  ? "text-success"
                  : "text-destructive"
              }`}
            >
              {mcpMetrics.errorRate.trend} vs yesterday
            </p>
          </CardContent>
        </Card>
      </div> */}
      <div className="grid @3xl:grid-cols-[2fr_500px] gap-4">
        {/* Tool Usage Analytics */}
        {/* Request Traces */}
        <McpRequestTraces
          projectId={projectId}
          traces={enhancedTraces}
          isLoading={tracesLoading}
          error={tracesError}
          initialExpandedTraceId={expandedTraceId}
        />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-lg">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Grouped traces
              </div>
              <FpDropdownMenu>
                <FpDropdownMenuTrigger className="flex items-center gap-1 px-2 py-1 text-sm border rounded hover:bg-muted">
                  <SortDesc className="w-4 h-4" />
                  Sort by{" "}
                  {sortBy === "calls"
                    ? "most calls"
                    : sortBy === "duration"
                      ? "slowest avg"
                      : "most recent"}
                </FpDropdownMenuTrigger>
                <FpDropdownMenuContent align="end">
                  <FpDropdownMenuItem onClick={() => setSortBy("recent")}>
                    Most recent
                  </FpDropdownMenuItem>
                  <FpDropdownMenuItem onClick={() => setSortBy("calls")}>
                    Most calls
                  </FpDropdownMenuItem>
                  <FpDropdownMenuItem onClick={() => setSortBy("duration")}>
                    Slowest average
                  </FpDropdownMenuItem>
                </FpDropdownMenuContent>
              </FpDropdownMenu>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {toolAnalytics.map((tool) => {
                const isExpanded = expandedTool === tool.name;
                const filteredTraces = getFilteredTraces(tool.name);

                return (
                  <div key={tool.name} className="rounded-lg border">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors text-left"
                      onClick={() => toggleTool(tool.name)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            )}
                            <span className="font-mono text-sm font-medium">
                              {tool.name}
                            </span>
                          </div>
                          <div>{tool.successRate}% success</div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{tool.calls} calls</span>
                          <span>Avg: {tool.avgDuration}ms</span>
                          <span>{filteredTraces.length} traces</span>
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-muted">
                        <McpRequestTraces
                          projectId={projectId}
                          traces={filteredTraces}
                          isLoading={tracesLoading}
                          error={tracesError}
                          className="border-0 shadow-none bg-muted/30"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Response Times Summary */}
        {/* <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Response Time Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold">
                  {mcpMetrics.responseTime.p50}ms
                </p>
                <p className="text-sm text-muted-foreground">P50 (median)</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">
                  {mcpMetrics.responseTime.p95}ms
                </p>
                <p className="text-sm text-muted-foreground">P95</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">
                  {mcpMetrics.responseTime.p99}ms
                </p>
                <p className="text-sm text-muted-foreground">P99</p>
              </div>
            </div>
          </CardContent>
        </Card> */}
      </div>
    </div>
  );
}
